import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { forwardRef, useState } from "react";
import TextView from "./Typography";
import { FIELD_VARIANT_INLINE, FIELD_VARIANT_BLOCK } from "../../models/Constants";
import { isEmpty } from "../../helpers/Functions";

const BaseTextField = forwardRef(
	(
		{
			variant = FIELD_VARIANT_BLOCK,
			titleclass = "",
			containerClass = "",
			display = "block",
			onKeyDown,
			value,
			tooltip = null,
			size = "small",
			onChange,
			multiline = false,
			willMaximize = false,
			...props
		},
		ref
	) => {
		const [isModalOpen, setIsModalOpen] = useState(false);
		let mainClass = isEmpty(containerClass) ? "w-100 mb-3" : containerClass;
		titleclass = `${titleclass} d-flex align-items-center`;

		if (variant === FIELD_VARIANT_BLOCK) {
			titleclass = `${titleclass} mb-2`;
		} else if (variant === FIELD_VARIANT_INLINE) {
			display = "flex";
			titleclass = `${titleclass} me-3 ${props.error ? "mb-3" : ""}`;
			mainClass = `${mainClass} align-items-center`;
		}

		mainClass = `${mainClass} d-${display} ${props?.customclasses}`;

		const handleKeyDown = (e) => {
			if (e.key === "Enter") {
				if (e.shiftKey) {
					e.preventDefault();
					onChange?.({ target: { value: value + "\n" } });
				} else {
					onKeyDown?.(e);
				}
			}
		};

		const handleExpandClick = () => {
			setIsModalOpen(true);
		};

		const handleModalClose = () => {
			setIsModalOpen(false);
		};

		return (
			<div className={mainClass}>
				<div className="d-flex justify-content-between">
					{props.title && (
						<TextView
							tooltip={tooltip}
							fontsize="medium"
							fontcolor="septenary"
							style={{ textWrap: "nowrap" }}
							className={titleclass}
						>
							{props.title}
						</TextView>
					)}

					{props?.extralink && props.extralink}
				</div>

				<div style={{ position: "relative", width: "100%" }}>
					<TextField
						className="w-100"
						InputProps={{
                            className: { 
                                input: "content-textfield",
                                ...(props.InputProps?.className || {})
                            },
                            ...(props.InputProps || {})
                        }}
						sx={{
							"& .MuiOutlinedInput-root": {
								"&:hover fieldset": {
									borderColor: "gray",
								},
								"&.Mui-focused fieldset": {
									borderColor: "gray",
								},
							},
						}}
						value={value}
						size={size}
						onChange={onChange}
						onKeyDown={handleKeyDown}
						multiline={multiline}
						ref={ref}
						{...props}
					/>
					{willMaximize && (
						<IconButton
							size="small"
							onClick={handleExpandClick}
							sx={{
								position: "absolute",
								bottom: 8,
								right: 8,
								backgroundColor: "rgba(255, 255, 255, 0.8)",
								"&:hover": {
									backgroundColor: "rgba(255, 255, 255, 0.9)",
								},
							}}
						>
							<OpenInFullIcon fontSize="small" />
						</IconButton>
					)}
				</div>

				<Dialog
					open={isModalOpen}
					onClose={handleModalClose}
					maxWidth="md"
					fullWidth
				>
					<IconButton
						aria-label="close"
						onClick={handleModalClose}
						sx={{
							position: 'absolute',
							right: 0,
							top: 0,
							color: "var(--theme-secondary)",
						}}
					>
						<CloseIcon />
					</IconButton>
					<DialogContent sx={{ p: 2 }}>
						<TextField
							fullWidth
							multiline
							value={value}
							onChange={onChange}
							variant="outlined"
							onKeyDown={handleKeyDown}
							placeholder={props.placeholder}
							{...props}
							rows={15}
							sx={{ mt: 2 }}
						/>
					</DialogContent>
				</Dialog>
			</div>
		);
	}
);

export default BaseTextField;
